import {
  resolveOrderStatusColor,
  resolveOrderStatusLabel,
} from '../../product-master/pre-ipo/order-management/orderStatus';
import { PERMISSIONS } from '../../../config/permissions';

const PAYMENT_PENDING_STATUSES = new Set(['ORDER_PLACED', 'AWAITING_CONFIRMATION']);

export function getInvestorPreIpoStatusLabel(statusOrRow) {
  if (statusOrRow && typeof statusOrRow === 'object') {
    return resolveOrderStatusLabel(statusOrRow);
  }
  const status = statusOrRow;
  if (!status) return status;
  if (PAYMENT_PENDING_STATUSES.has(status)) return 'Payment Pending';
  return resolveOrderStatusLabel({ status });
}

export function getInvestorPreIpoStatusColor(statusOrRow) {
  if (statusOrRow && typeof statusOrRow === 'object') {
    return resolveOrderStatusColor(statusOrRow);
  }
  const status = statusOrRow;
  if (PAYMENT_PENDING_STATUSES.has(status)) return 'blue';
  return resolveOrderStatusColor({ status });
}

export function formatInvestorCurrency(value) {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount);
}

export function formatInvestorFee(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return '—';
  return formatInvestorCurrency(n);
}

/** Cache-bust deal sheet PDF URLs so browsers fetch the latest file after regenerate. */
export function dealSheetPdfHref(url, generatedAt) {
  if (!url) return null;
  if (!generatedAt) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}v=${encodeURIComponent(generatedAt)}`;
}

export function buildDealSheetPdfFilename(row, investorName, generatedAt) {
  const slugify = (value, maxLen = 60) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, maxLen);

  const ts = generatedAt ?? row?.manual_deal_sheet_generated_at;
  const timestamp = ts ? new Date(ts).getTime() : Date.now();
  const nameSlug = slugify(investorName || row?.investor_name) || 'investor';

  return `${nameSlug}-${timestamp}.pdf`;
}

/** Direct anchor navigation — avoids CORS on cross-origin PDF URLs (S3/CDN). */
export function triggerDealSheetPdfDownload(pdfUrl, filename = 'deal-sheet.pdf') {
  if (!pdfUrl) return;
  const a = document.createElement('a');
  a.href = pdfUrl;
  a.download = filename;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** Match altcase-api dealSheetStaticConfig rates / computeDealSheetAmounts. */
const DEAL_SHEET_STAMP_DUTY_RATE = 0.00015;
const DEAL_SHEET_TDS_RATE = 0.001;

function getDealSheetLineAmount(row) {
  const fromColumn = Number(row?.total_amount);
  if (Number.isFinite(fromColumn) && fromColumn > 0) return fromColumn;
  const qty = Number(row?.requested_qty ?? row?.quantity ?? 0);
  const price = Number(row?.price ?? row?.unit_price ?? 0);
  const computed = qty * price;
  if (Number.isFinite(computed) && computed > 0) return computed;
  return Number(row?.amount ?? 0);
}

/** Fee amount shown in table — mirrors deal sheet PDF computation, not raw order columns alone. */
export function getDealSheetDisplayFee(row, feeKey) {
  const options = row?.manual_deal_sheet_options;
  const hasDealSheet = Boolean(row?.manual_deal_sheet_url && options);

  if (feeKey === 'stamp_duty') {
    if (hasDealSheet && options.include_stamp_duty === false) return 0;
    const stored = Number(row?.stamp_duty);
    if (Number.isFinite(stored) && stored > 0) return stored;
    if (hasDealSheet && options.include_stamp_duty !== false) {
      return parseFloat((getDealSheetLineAmount(row) * DEAL_SHEET_STAMP_DUTY_RATE).toFixed(2));
    }
    return stored;
  }

  if (feeKey === 'tcs') {
    if (hasDealSheet && options.include_tds !== true) return 0;
    const stored = Number(row?.tcs);
    if (Number.isFinite(stored) && stored > 0) return stored;
    if (hasDealSheet && options.include_tds === true) {
      return parseFloat((getDealSheetLineAmount(row) * DEAL_SHEET_TDS_RATE).toFixed(2));
    }
    return stored;
  }

  return 0;
}

/** Net payable for table — prefer stored total_payable; else mirror deal sheet PDF math. */
export function getDealSheetDisplayAmount(row) {
  const storedPayable = Number(row?.total_payable);
  if (Number.isFinite(storedPayable)) {
    return storedPayable;
  }
  if (!row?.manual_deal_sheet_url || !row?.manual_deal_sheet_options) {
    return Number(row?.amount ?? 0);
  }
  const lineAmount = getDealSheetLineAmount(row);
  const stampDuty = getDealSheetDisplayFee(row, 'stamp_duty');
  const tds = getDealSheetDisplayFee(row, 'tcs');
  return parseFloat((lineAmount + stampDuty - tds).toFixed(2));
}

export function formatDealSheetAmount(row) {
  return formatInvestorCurrency(getDealSheetDisplayAmount(row));
}

/** Stamp duty column: reflects deal sheet options when a manual deal sheet exists. */
export function formatDealSheetStampDuty(row) {
  return formatInvestorFee(getDealSheetDisplayFee(row, 'stamp_duty'));
}

/** TDS column: reflects deal sheet options when a manual deal sheet exists. */
export function formatDealSheetTds(row) {
  return formatInvestorFee(getDealSheetDisplayFee(row, 'tcs'));
}

export function formatInvestorTransDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

/** Investor profile `?tab=` value for Pre-IPO Transactions */
export const INVESTOR_PROFILE_TRANSACTIONS_TAB = 'pre_ipo_transactions_all';

export const INVESTOR_PROFILE_TAB_KEYS = {
  portfolio: 'portfolio',
  personal: 'personal_info',
  bankDetails: 'bank_details',
  dematDetails: 'demat_details',
  address: 'address',
  professional: 'professional',
  nominee: 'nominee',
  preIpoTransactions: INVESTOR_PROFILE_TRANSACTIONS_TAB,
};

/** Permission required to view each investor profile tab */
export const INVESTOR_PROFILE_TAB_PERMISSIONS = {
  [INVESTOR_PROFILE_TAB_KEYS.portfolio]: PERMISSIONS.INVESTORS_PORTFOLIO_VIEW,
  [INVESTOR_PROFILE_TAB_KEYS.personal]: PERMISSIONS.INVESTORS_PERSONAL_INFO_VIEW,
  [INVESTOR_PROFILE_TAB_KEYS.bankDetails]: PERMISSIONS.INVESTORS_BANK_DETAILS_VIEW,
  [INVESTOR_PROFILE_TAB_KEYS.dematDetails]: PERMISSIONS.INVESTORS_DEMAT_DETAILS_VIEW,
  [INVESTOR_PROFILE_TAB_KEYS.address]: PERMISSIONS.INVESTORS_ADDRESS_VIEW,
  [INVESTOR_PROFILE_TAB_KEYS.professional]: PERMISSIONS.INVESTORS_PROFESSIONAL_VIEW,
  [INVESTOR_PROFILE_TAB_KEYS.nominee]: PERMISSIONS.INVESTORS_NOMINEE_VIEW,
  [INVESTOR_PROFILE_TAB_KEYS.preIpoTransactions]: PERMISSIONS.INVESTORS_PRE_IPO_TRANSACTIONS_VIEW,
};

export function canViewInvestorProfileTab(tabKey, hasPermission, isSuperAdmin) {
  if (isSuperAdmin) return true;
  const permission = INVESTOR_PROFILE_TAB_PERMISSIONS[tabKey];
  if (!permission) return true;
  return hasPermission(permission);
}

export function getAllowedInvestorProfileTabs(hasPermission, isSuperAdmin) {
  return Object.values(INVESTOR_PROFILE_TAB_KEYS).filter((tabKey) =>
    canViewInvestorProfileTab(tabKey, hasPermission, isSuperAdmin)
  );
}

export function resolveInvestorProfileTab(tabFromUrl, hasPermission, isSuperAdmin) {
  const allowedTabs = getAllowedInvestorProfileTabs(hasPermission, isSuperAdmin);
  const defaultTab = allowedTabs[0] || INVESTOR_PROFILE_TAB_KEYS.portfolio;
  if (tabFromUrl && allowedTabs.includes(tabFromUrl)) return tabFromUrl;
  return defaultTab;
}

export function mapInvestorProfileSectionToTab(section) {
  if (section === 'bank_details') return INVESTOR_PROFILE_TAB_KEYS.bankDetails;
  return '';
}

export function resolveInvestorProfileTabFromParams({ tab, section, hasPermission, isSuperAdmin }) {
  const tabKey = tab || mapInvestorProfileSectionToTab(section);
  return resolveInvestorProfileTab(tabKey, hasPermission, isSuperAdmin);
}
